import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const HTMLComponents = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Modern HTML</div>
                <h1>Web Components & Modern HTML</h1>
                <p className="hero-lead">
                    Build reusable, encapsulated components with Web Components. Learn Custom Elements,
                    Shadow DOM, and HTML Templates for modern, framework-independent web development.
                </p>
            </div>

            <div className="content-section">
                <h2>1. The Evolution of the Web: Component-Driven Architecture</h2>
                <p>
                    Web Components represent a fundamental shift from page-based development
                    to <strong>Component-Based Architecture</strong>. In the early web,
                    sites were loosely coupled HTML documents; today, modern applications
                    are built from independent, reusable units of logic and UI.
                    While frameworks like React and Vue brought these concepts to the mainstream,
                    the Web Components standard brings these capabilities directly to the
                    browser's core engine, ensuring forward-compatibility and performance.
                </p>
                <div className="concept-grid">
                    <div className="tech-card">
                        <h3>Interoperability</h3>
                        <p>Web Components are "Framework Agnostic." A component built with
                            standard APIs works in React, Angular, Svelte, or vanilla HTML.
                            This prevents "Framework Lock-in" and ensures your UI library
                            can survive across major technology shifts.</p>
                    </div>
                    <div className="tech-card">
                        <h3>Native Encapsulation</h3>
                        <p>Unlike framework components that rely on abstraction layers for
                            styling, Web Components use the browser-native Shadow DOM to
                            guarantee that internal styles never leak out and global
                            styles never accidentally break your component.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>2. The Encapsulation Boundary: Shadow DOM vs. Light DOM</h2>
                <p>
                    The <strong>Shadow DOM</strong> is arguably the most powerful pillar of
                    the Web Components specification. It allows you to attach a separate,
                    hidden DOM tree to an elementthe <strong>Shadow Root</strong>.
                    This tree is rendered by the browser but remains inaccessible to standard
                    JavaScript selectors like <code>document.getElementById</code> or
                    global CSS rules.
                </p>
                <ProTip><p><strong>Professional Insight:</strong> Most developers use
                        <code>mode: 'open'</code> when attaching a Shadow Root. While
                        <code>'closed'</code> offers stricter isolation, it makes it
                        extremely difficult to perform automated testing or debugging on
                        the component's internal state.</p></ProTip>
                <div className="concept-grid">
                    <div className="tech-card">
                        <h3>The Light DOM Boundary</h3>
                        <p>The "Standard" DOM where your custom element lives. Content
                            placed inside your custom tag by a user (e.g.,
                            <code>&lt;my-tag&gt;Text&lt;/my-tag&gt;</code>) resides in the
                            Light DOM until it is "projected" into your component via Slots.</p>
                    </div>
                    <div className="tech-card">
                        <h3>The Scoped CSS Tree</h3>
                        <p>Inside the Shadow DOM, CSS selectors are globally unique to that
                            instance. A rule like <code>{'h1 {color: red; }'}</code> will
                            only affect the H1 inside your component, leaving all other
                            H1s on the page untouched.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Custom Elements</h2>

                <h3>Basic Custom Element</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Define custom element --&gt;
<script>
class UserCard extends HTMLElement {
    constructor() {
        super();
        // Element is created
    }
    
    connectedCallback() {
        // Element added to DOM
        this.innerHTML = \`
            <div class="user-card">
                <img src="\${this.getAttribute('avatar')}" alt="Avatar">
                <h3>\${this.getAttribute('name')}</h3>
                <p>\${this.getAttribute('bio')}</p>
            </div>
        \`;
    }
    
    disconnectedCallback() {
        // Element removed from DOM
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
        // Attribute changed
    }
    
    static get observedAttributes() {
        return ['name', 'bio', 'avatar'];
    }
}

// Register custom element
customElements.define('user-card', UserCard);
</script>

<!-- Use custom element --&gt;
<user-card 
    name="John Doe"
    bio="Software Developer"
    avatar="/avatar.jpg">
</user-card>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Shadow DOM</h2>
                <p>
                    Shadow DOM provides encapsulation - styles and scripts don't leak in or out.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<script>
class FancyButton extends HTMLElement {
    constructor() {
        super();
        
        // Attach shadow DOM (encapsulated)
        const shadow = this.attachShadow({ mode: 'open' });
        
        // Create button
        const button = document.createElement('button');
        button.textContent = this.textContent;
        
        // Create style (scoped to shadow DOM)
        const style = document.createElement('style');
        style.textContent = \`
            button {
                padding: 12px 24px;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                border: none;
                border-radius: 8px;
                cursor: pointer;
                font-size: 16px;
                transition: transform 0.2s;
            }
            
            button:hover {
                transform: scale(1.05);
            }
            
            button:active {
                transform: scale(0.95);
            }
        \`;
        
        // Append to shadow DOM
        shadow.appendChild(style);
        shadow.appendChild(button);
    }
}

customElements.define('fancy-button', FancyButton);
</script>

<!-- Use fancy button --&gt;
<fancy-button>Click Me!</fancy-button>

<!-- Styles from outside don't affect it --&gt;
<style>
button { background: red; } /* Doesn't affect fancy-button */
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>HTML Templates</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Define template --&gt;
<template id="user-template">
    <style>
        .user-card {
            border: 1px solid #ddd;
            padding: 20px;
            border-radius: 8px;
            display: flex;
            gap: 15px;
            align-items: center;
        }
        
        .avatar {
            width: 60px;
            height: 60px;
            border-radius: 50%;
        }
        
        .user-info h3 {
            margin: 0 0 5px 0;
        }
        
        .user-info p {
            margin: 0;
            color: #666;
        }
    </style>
    
    <div class="user-card">
        <img class="avatar" alt="Avatar">
        <div class="user-info">
            <h3 class="name"></h3>
            <p class="bio"></p>
        </div>
    </div>
</template>

<script>
class UserCard extends HTMLElement {
    constructor() {
        super();
        
        // Get template
        const template = document.getElementById('user-template');
        const content = template.content.cloneNode(true);
        
        // Attach shadow DOM
        const shadow = this.attachShadow({ mode: 'open' });
        shadow.appendChild(content);
        
        // Populate data
        shadow.querySelector('.avatar').src = this.getAttribute('avatar');
        shadow.querySelector('.name').textContent = this.getAttribute('name');
        shadow.querySelector('.bio').textContent = this.getAttribute('bio');
    }
}

customElements.define('user-card', UserCard);
</script>

<!-- Use component --&gt;
<user-card 
    name="Jane Smith"
    bio="UX Designer"
    avatar="/jane.jpg">
</user-card>
<user-card 
    name="Bob Johnson"
    bio="Data Scientist"
    avatar="/bob.jpg">
</user-card>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Slots (Content Projection)</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<script>
class CardComponent extends HTMLElement {
    constructor() {
        super();
        
        const shadow = this.attachShadow({ mode: 'open' });
        
        shadow.innerHTML = \`
            <style>
                .card {
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    padding: 20px;
                    margin: 10px 0;
                }
                
                .card-header {
                    font-weight: bold;
                    margin-bottom: 10px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #eee;
                }
                
                .card-body {
                    margin-bottom: 10px;
                }
                
                .card-footer {
                    color: #666;
                    font-size: 0.9em;
                }
            </style>
            
            <div class="card">
                <div class="card-header">
                    <slot name="header">Default Header</slot>
                </div>
                <div class="card-body">
                    <slot>Default content</slot>
                </div>
                <div class="card-footer">
                    <slot name="footer"></slot>
                </div>
            </div>
        \`;
    }
}

customElements.define('card-component', CardComponent);
</script>

<!-- Use with slots --&gt;
<card-component>
    <span slot="header">Custom Header</span>
    <p>This is the main content of the card.</p>
    <span slot="footer">Last updated: 2024</span>
</card-component>

<card-component>
    <h2 slot="header">Product Card</h2>
    <div>
        <img src="product.jpg" alt="Product">
        <p>$99.99</p>
    </div>
    <button slot="footer">Add to Cart</button>
</card-component>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>6. Deep Dive: The Web Component Lifecycle</h2>
                <p>
                    Understanding the lifecycle is critical for managing memory and ensuring
                    high performance. Every Web Component goes through a set of predefined
                    phases that you can hook into.
                </p>
                <div className="concept-grid">
                    <div className="tech-card">
                        <h3>1. Registration Phase</h3>
                        <p>The browser verifies the tag name (which must contain a hyphen)
                            and links it to your class constructor. This is a one-time
                            overhead that occurs when the script is loaded.</p>
                    </div>
                    <div className="tech-card">
                        <h3>2. Initialization (Constructor)</h3>
                        <p>The element is created. <strong>Rule of thumb:</strong> Only
                            initialize state and attach the Shadow DOM here. Do not
                            access attributes or children, as they may not be
                            available yet.</p>
                    </div>
                    <div className="tech-card">
                        <h3>3. Mounting (Connected)</h3>
                        <p>The element enters the DOM. This is where you should perform
                            data fetching, set up event listeners, and start
                            animation loops.</p>
                    </div>
                    <div className="tech-card">
                        <h3>4. Reaction (Attribute Changes)</h3>
                        <p>The <code>attributeChangedCallback</code> allows your component
                            to be "Reactive." When an observed attribute changes, you
                            can trigger an internal re-render or update specific
                            parts of the shadow tree.</p>
                    </div>
                </div>
                <ProTip><p><strong>Senior Engineer Tip:</strong> Always clean up in the
                        <code>disconnectedCallback</code>. If you add a global event
                        listener (e.g., to <code>window</code> or <code>document</code>),
                        failure to remove it when the component is destroyed will
                        lead to <strong>Memory Leaks</strong> that can crash the
                        user's tab over time.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>7. Modern Architecture: Micro-frontends with Web Components</h2>
                <p>
                    Web Components are the primary technology behind
                    <strong>Micro-frontend Architectures</strong>. They allow large
                    organizations to build parts of a single page using different
                    teams (and even different frameworks), and then "stitch" them
                    together into a cohesive user experience.
                </p>
                <div className="concept-grid">
                    <div className="tech-card">
                        <h3>Isolated Responsibility</h3>
                        <p>A checkout team can build a <code>&lt;payment-portal&gt;</code>
                            component in React, while the search team builds a
                            <code>&lt;product-grid&gt;</code> in Vue. Both are
                            exported as Web Components and wrapped in a vanilla
                            HTML shell.</p>
                    </div>
                    <div className="tech-card">
                        <h3>Version Independence</h3>
                        <p>Because of the Shadow DOM, one component can use
                            version 1.0 of a library while another uses version 2.0
                            without any global namespace conflicts. This is
                            impossible with traditional JavaScript modules.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <div className="code-example">
                    <CodeBlock language="javascript" code={`class MyElement extends HTMLElement {
    constructor() {
        super();
        console.log('1. Element created (constructor)');
        // Initialize state, create shadow DOM
        // Don't modify attributes or children here
    }
    
    connectedCallback() {
        console.log('2. Element added to DOM');
        // Setup, render, add event listeners
        // Safe to modify DOM here
    }
    
    disconnectedCallback() {
        console.log('3. Element removed from DOM');
        // Cleanup: remove event listeners, cancel timers
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
        console.log(\`4. Attribute "\${name}" changed from "\${oldValue}" to "\${newValue}"\`);
        // React to attribute changes
        // Update component based on new value
    }
    
    adoptedCallback() {
        console.log('5. Element moved to new document');
        // Rarely used (iframe scenarios)
    }
    
    // Define which attributes to observe
    static get observedAttributes() {
        return ['name', 'value', 'disabled'];
    }
}

customElements.define('my-element', MyElement);`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Practical Example: Todo List Component</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<template id="todo-list-template">
    <style>
        .todo-list {
            max-width: 400px;
            margin: 0 auto;
            font-family: Arial, sans-serif;
        }
        
        .todo-input {
            display: flex;
            gap: 10px;
            margin-bottom: 20px;
        }
        
        input {
            flex: 1;
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 4px;
        }
        
        button {
            padding: 10px 20px;
            background: #4CAF50;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
        }
        
        ul {
            list-style: none;
            padding: 0;
        }
        
        li {
            padding: 10px;
            background: #f5f5f5;
            margin: 5px 0;
            border-radius: 4px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        li.completed {
            text-decoration: line-through;
            opacity: 0.6;
        }
        
        .delete-btn {
            background: #f44336;
            padding: 5px 10px;
            font-size: 12px;
        }
    </style>
    
    <div class="todo-list">
        <div class="todo-input">
            <input type="text" placeholder="Add new todo..." />
            <button class="add-btn">Add</button>
        </div>
        <ul class="todo-items"></ul>
    </div>
</template>

<script>
class TodoList extends HTMLElement {
    constructor() {
        super();
        
        this.todos = [];
        
        const template = document.getElementById('todo-list-template');
        const content = template.content.cloneNode(true);
        
        const shadow = this.attachShadow({ mode: 'open' });
        shadow.appendChild(content);
    }
    
    connectedCallback() {
        const shadow = this.shadowRoot;
        const input = shadow.querySelector('input');
        const addBtn = shadow.querySelector('.add-btn');
        
        addBtn.addEventListener('click', () => this.addTodo(input.value));
        input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') this.addTodo(input.value);
        });
    }
    
    addTodo(text) {
        if (!text.trim()) return;
        
        const todo = {
            id: Date.now(),
            text: text.trim(),
            completed: false
        };
        
        this.todos.push(todo);
        this.render();
        
        const input = this.shadowRoot.querySelector('input');
        input.value = '';
    }
    
    toggleTodo(id) {
        const todo = this.todos.find(t => t.id === id);
        if (todo) {
            todo.completed = !todo.completed;
            this.render();
        }
    }
    
    deleteTodo(id) {
        this.todos = this.todos.filter(t => t.id !== id);
        this.render();
    }
    
    render() {
        const ul = this.shadowRoot.querySelector('.todo-items');
        ul.innerHTML = '';
        
        this.todos.forEach(todo => {
            const li = document.createElement('li');
            li.className = todo.completed ? 'completed' : '';
            li.innerHTML = \`
                <span>\${todo.text}</span>
                <button class="delete-btn">Delete</button>
            \`;
            
            li.querySelector('span').addEventListener('click', () => this.toggleTodo(todo.id));
            li.querySelector('.delete-btn').addEventListener('click', () => this.deleteTodo(todo.id));
            
            ul.appendChild(li);
        });
    }
}

customElements.define('todo-list', TodoList);
</script>

<!-- Use component --&gt;
<h1>My Todos</h1>
<todo-list></todo-list>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Browser Support</h2>
                <p>Web Components are supported in all modern browsers:</p>
                <ul>
                    <li>Chrome 67+</li>
                    <li>Firefox 63+</li>
                    <li>Safari 10.1+</li>
                    <li>Edge 79+ (Chromium)</li>
                </ul>

                <h3>Polyfills for Older Browsers</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Load polyfills for older browsers --&gt;
<script src="https://unpkg.com/@webcomponents/webcomponentsjs@latest/webcomponents-loader.js"></script>
<script src="https://unpkg.com/@webcomponents/custom-elements@latest/custom-elements.min.js"></script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>11. Accessibility in Web Components</h2>
                <p>
                    A common Pitfall of Web Components is that the Shadow DOM can
                    "Hide" elements from assistive technologies if not implemented
                    correctly. To bridge this gap, you must use <strong>ARIA Roles</strong>
                    and <strong>Delegated Focus</strong>.
                </p>
                <div className="concept-grid">
                    <div className="tech-card">
                        <h3>ARIA Reflection</h3>
                        <p>Attributes like <code>role</code> and <code>aria-label</code>
                            should be "reflected" from the custom element to the
                            appropriate internal element within the shadow root.
                            This ensures that screen readers interpret the
                            component as a cohesive interactive unit.</p>
                    </div>
                    <div className="tech-card">
                        <h3>DelegatesFocus</h3>
                        <p>When attaching a shadow root, setting <code>delegatesFocus: true</code>
                            ensures that when a user clicks a non-focusable part
                            of the component, the first focusable internal element
                            (like an <code>&lt;input&gt;</code>) automatically
                            receives focus.</p>
                    </div>
                </div>
                <ProTip><p><strong>Accessibility Standard:</strong> Always provide
                        fallback content in your <code>&lt;slot&gt;</code> elements.
                        This ensures that if the user doesn't provide the
                        expected content, the component remains usable and
                        semantically valid for all users.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>12. Theoretical Deep Dive: The Custom Elements Registry</h2>
                <p>
                    Every time you call <code>customElements.define()</code>, you are
                    interacting with the browser's <strong>Custom Elements Registry</strong>.
                    This registry is a global object that maps tag names to class
                    definitions.
                </p>
                <p>
                    When the browser parses an HTML document and encounters a tag
                    like <code>&lt;my-card&gt;</code>, it looks it up in the registry.
                    If it finds a match, it <strong>Upgrades</strong> the element from a
                    generic <code>HTMLUnknownElement</code> to your specific class.
                    This process is asynchronous and allows you to load your
                    component definitions late (lazy-loading) without breaking the
                    initial page render.
                </p>
                <ProTip><p><strong>Professional Insight:</strong> Use the
                        <code>customElements.whenDefined('my-tag')</code> promise
                        to wait for a component to be registered before
                        interacting with its properties. This prevents "Race Conditions"
                        where your script tries to call a method on a component
                        that haven't been upgraded by the browser yet.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>13. Web Components Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use shadow DOM for encapsulation</li>
                            <li>Define clear component APIs</li>
                            <li>Use templates for reusable markup</li>
                            <li>Observe only necessary attributes</li>
                            <li>Clean up in disconnectedCallback</li>
                            <li>Use slots for content projection</li>
                            <li>Follow naming conventions (dash-case)</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Modify DOM in constructor</li>
                            <li>Use single-word tag names</li>
                            <li>Forget to remove event listeners</li>
                            <li>Break encapsulation</li>
                            <li>Ignore accessibility</li>
                            <li>Create heavy components</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Web Components vs Frameworks</h2>

                <table className="comparison-table">
                    <thead>
                        <tr>
                            <th>Feature</th>
                            <th>Web Components</th>
                            <th>React/Vue</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Standard</strong></td>
                            <td>Web standard</td>
                            <td> Framework-specific</td>
                        </tr>
                        <tr>
                            <td><strong>Dependencies</strong></td>
                            <td>None (native)</td>
                            <td> Framework required</td>
                        </tr>
                        <tr>
                            <td><strong>Encapsulation</strong></td>
                            <td>Shadow DOM</td>
                            <td> CSS Modules/styled-components</td>
                        </tr>
                        <tr>
                            <td><strong>Ecosystem</strong></td>
                            <td> Growing</td>
                            <td>Mature</td>
                        </tr>
                        <tr>
                            <td><strong>Performance</strong></td>
                            <td>Lightweight</td>
                            <td> Depends on framework</td>
                        </tr>
                        <tr>
                            <td><strong>Learning Curve</strong></td>
                            <td> Moderate</td>
                            <td> Framework-specific</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>14. Enterprise-Scale Component Management</h2>
                <p>
                    In massive engineering organizations, Web Components are managed using
                    <strong>Design Systems</strong>. Instead of hundreds of independent
                    developers defining their own elements, a centralized team creates
                    a library of pre-vetted, high-quality components that are then
                    distributed across the company.
                </p>
                <div className="concept-grid">
                    <div className="tech-card">
                        <h3>Self-Documenting Code</h3>
                        <p>High-quality Web Components are self-documenting. By using
                            consistent attribute naming and providing clear
                            <code>jsdoc</code> comments for methods, developers can
                            easily discover how to use a component just by
                            inspecting its interface in the browser DevTools.</p>
                    </div>
                    <div className="tech-card">
                        <h3>Automated Testing</h3>
                        <p>Enterprise components are tested using tools like
                            <strong>Web Test Runner</strong>. These tools instantiate
                            the component in a real browser environment and
                            audit its behavior against performance and
                            accessibility benchmarks on every commit.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>15. The Future: Forward-Compatibility Strategies</h2>
                <p>
                    One of the greatest strengths of Web Components is their
                    <strong>Forward-Compatibility</strong>. Unlike framework-specific
                    components that may break when a library updates (e.g., from React
                    Class Components to Hooks), Web Components are based on
                    standardized browser APIs that are guaranteed to work for
                    the foreseeable future.
                </p>
                <ProTip><p><strong>Senior Strategy:</strong> When building a long-term
                        platform, wrap your core business logic in Web Components.
                        Even if you decide to change your main application framework
                        three years from now, your core building blocks will
                        remain fully functional and won't require a rewrite.</p></ProTip>
                <div className="concept-grid">
                    <div className="tech-card">
                        <h3>Scoped Custom Element Registries</h3>
                        <p>A new specification called "Scoped Registries" will allow
                            different parts of an application to use different versions
                            of the same custom element without conflict, further
                            enhancing the isolation required for complex,
                            enterprise-scale micro-frontends.</p>
                    </div>
                    <div className="tech-card">
                        <h3>Declarative Shadow DOM</h3>
                        <p>This allows you to define the shadow tree's structure
                            directly in HTML (without JavaScript), enabling
                            <strong>Server-Side Rendering (SSR)</strong> for Web Components.
                            This is essential for SEO-heavy pages that need the
                            benefits of encapsulation without sacrificing First Contentful Paint.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>16. Strategic Benefit: Declarative Shadow DOM for SEO</h2>
                <p>
                    Historically, a major criticism of Web Components was their reliance on
                    JavaScript for rendering, which could hinder
                    <strong>Search Engine Optimization (SEO)</strong>. However, the introduction of
                    <strong>Declarative Shadow DOM (DSD)</strong> has solved this challenge.
                    DSD allows the browser to parse and render the shadow tree's structure
                    directly from the initial HTML stream, without waiting for
                    JavaScript to execute.
                </p>
                <ProTip><p><strong>SEO Professional Tip:</strong> If your component
                        contains critical content (like headers, product descriptions,
                        or pricing), always use Declarative Shadow DOM or provide
                        a meaningful Light DOM fallback. This ensures that
                        search engine crawlers can index your content immediately,
                        improving your site's ranking and discoverability.</p></ProTip>
            </div>

        </div >
    );
};

export default HTMLComponents;



